import React, { useState, useRef, useCallback, useEffect } from 'react';
import './SalonImageCarousel.css';

// Import placeholder images
import appointmentPlaceholder from '../../assets/images/professional-signup/appointment.png';
import barberPlaceholder from '../../assets/images/professional-signup/barber.png';
import hairdresserPlaceholder from '../../assets/images/professional-signup/hairdresser.png';
import nailTechPlaceholder from '../../assets/images/professional-signup/nail-tech.png';

/**
 * Progressive image component with loading state and blur-up effect
 */
const ProgressiveImage = ({ src, alt, className, loading = 'lazy', onLoad }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    // Reset state when src changes
    setIsLoaded(false);
    setHasError(false);
  }, [src]);

  // Check if image is already cached
  useEffect(() => {
    if (imgRef.current && imgRef.current.complete && imgRef.current.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [src]);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  return (
    <div className={`progressive-image-wrapper ${isLoaded ? 'loaded' : 'loading'}`}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className={`${className} ${isLoaded ? 'image-loaded' : 'image-loading'}`}
        loading={loading}
        decoding="async"
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
};

/**
 * Get the appropriate placeholder image based on business type/category
 * @param {string} businessType - The type of business (barber, hair, beauty, etc.)
 * @param {string} primaryCategory - The primary category of the salon
 * @returns {string} - The placeholder image URL
 */
const getPlaceholderImage = (businessType, primaryCategory) => {
  const type = (businessType || primaryCategory || '').toLowerCase();
  
  // Check for barber-related keywords
  if (type.includes('barber') || type.includes('gentlemen') || type.includes('men')) {
    return barberPlaceholder;
  }
  
  // Check for hair-related keywords
  if (type.includes('hair') || type.includes('salon') || type.includes('stylist') || type.includes('styling')) {
    return hairdresserPlaceholder;
  }
  
  // Check for beauty/nail-related keywords
  if (type.includes('beauty') || type.includes('nail') || type.includes('spa') || type.includes('aesthet') || type.includes('makeup') || type.includes('lash') || type.includes('brow')) {
    return nailTechPlaceholder;
  }
  
  // Default placeholder
  return appointmentPlaceholder;
};

/**
 * SalonImageCarousel - A swipeable image carousel for salon cards
 * Displays main image + gallery images with touch/swipe support
 *
 * @param {Object} props
 * @param {string} props.mainImage - The main/headline image URL
 * @param {Array} props.galleryImages - Array of gallery image objects with url, caption, order
 * @param {string} props.altText - Alt text for images
 * @param {string} props.className - Additional CSS class
 * @param {Function} props.onClick - Click handler (called when not swiping)
 * @param {React.ReactNode} props.overlay - Optional overlay content (e.g., rating badge)
 * @param {string} props.businessType - The type of business for placeholder selection
 * @param {string} props.primaryCategory - The primary category for placeholder selection
 * @param {boolean} props.singleImage - If true, render only the primary image
 *   with no carousel UI (no dots, arrows, or touch handlers). Gallery images
 *   are ignored. Used by the Recommended Near You row on the marketplace
 *   where swiping is reserved for scrolling the card row.
 */
const SalonImageCarousel = ({
  mainImage,
  galleryImages = [],
  altText = 'Salon',
  className = '',
  onClick,
  overlay,
  businessType = '',
  primaryCategory = '',
  singleImage = false
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  // Live-follow drag (same owned-position model as the shop carousel):
  // the image tracks the finger via dragDelta, so the gesture feels
  // physical instead of only jumping on release.
  const [dragDelta, setDragDelta] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const dragRef = useRef({ active: false, startX: 0, delta: 0 });
  const containerRef = useRef(null);

  // Combine main image with gallery images, sorted by order
  // Avoid duplicating the main image if it's already in the gallery
  const allImages = React.useMemo(() => {
    const images = [];
    
    // Add main image first if it exists
    if (mainImage) {
      images.push({ url: mainImage, isMain: true });
    }
    
    // Add gallery images sorted by order, but skip if URL matches main image
    if (galleryImages && galleryImages.length > 0) {
      const sortedGallery = [...galleryImages]
        .filter(img => img.url && img.url !== '')
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      sortedGallery.forEach(img => {
        // Skip if this gallery image URL matches the main image URL
        // Compare the actual filename/path, not the full URL (in case of different CDN prefixes)
        const mainImageFilename = mainImage ? mainImage.split('/').pop() : '';
        const galleryImageFilename = img.url.split('/').pop();
        
        if (mainImageFilename && galleryImageFilename === mainImageFilename) {
          return; // Skip duplicate
        }
        
        images.push({ url: img.url, caption: img.caption, isMain: false });
      });
    }
    
    return images;
  }, [mainImage, galleryImages]);

  // Minimum swipe distance to trigger slide change
  const minSwipeDistance = 50;

  // Damp the live delta when dragging past the ends so the first/last
  // image rubber-bands instead of sliding into blank space.
  const dampDelta = useCallback((delta) => {
    const atStart = delta > 0 && currentIndex === 0;
    const atEnd = delta < 0 && currentIndex === allImages.length - 1;
    return atStart || atEnd ? delta * 0.25 : delta;
  }, [currentIndex, allImages.length]);

  const onTouchStart = useCallback((e) => {
    // This carousel owns horizontal gestures that start on it - without
    // this, ancestors with their own horizontal swipe (the marketplace
    // category feed) would ALSO drag, flipping the tab mid-gallery-swipe.
    e.stopPropagation();
    dragRef.current = { active: true, startX: e.targetTouches[0].clientX, delta: 0 };
    setIsSwiping(false);
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!dragRef.current.active) return;
    const delta = e.targetTouches[0].clientX - dragRef.current.startX;
    dragRef.current.delta = delta;
    setDragDelta(dampDelta(delta));
    if (Math.abs(delta) > 10) setIsSwiping(true);
  }, [dampDelta]);

  const onTouchEnd = useCallback(() => {
    if (!dragRef.current.active) return;
    const delta = dragRef.current.delta;
    dragRef.current.active = false;
    setDragDelta(0);

    if (delta < -minSwipeDistance && currentIndex < allImages.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else if (delta > minSwipeDistance && currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }

    // Reset after a short delay to prevent click from firing
    setTimeout(() => {
      setIsSwiping(false);
    }, 50);
  }, [currentIndex, allImages.length]);

  // Mouse drag for desktop parity - mirrors the touch path; a window-level
  // mouseup catches drags released outside the card.
  const onMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault(); // stop native image ghost-drag
    dragRef.current = { active: true, startX: e.clientX, delta: 0, mouse: true };
    setIsSwiping(false);
  }, []);

  const onMouseMove = useCallback((e) => {
    if (!dragRef.current.active || !dragRef.current.mouse) return;
    const delta = e.clientX - dragRef.current.startX;
    dragRef.current.delta = delta;
    setDragDelta(dampDelta(delta));
    if (Math.abs(delta) > 10) setIsSwiping(true);
  }, [dampDelta]);

  useEffect(() => {
    const release = () => {
      if (dragRef.current.active && dragRef.current.mouse) onTouchEnd();
    };
    window.addEventListener('mouseup', release);
    return () => window.removeEventListener('mouseup', release);
  }, [onTouchEnd]);

  const handleClick = useCallback((e) => {
    if (isSwiping) {
      // A drag just ended here - swallow the click so the card underneath
      // doesn't navigate.
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    if (onClick) onClick(e);
  }, [isSwiping, onClick]);

  const goToSlide = useCallback((index, e) => {
    e.stopPropagation();
    setCurrentIndex(index);
  }, []);

  // Arrow navigation handlers
  const goToPrev = useCallback((e) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const goToNext = useCallback((e) => {
    e.stopPropagation();
    if (currentIndex < allImages.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  }, [currentIndex, allImages.length]);

  // Single-image mode: render only the primary image regardless of how
  // many gallery images exist. Used by the Recommended Near You row, which
  // reserves swipe for scrolling the parent card row rather than switching
  // images. No carousel UI (dots / arrows / touch handlers).
  if (singleImage) {
    // .single-image modifier relaxes touch-action so the parent card row can
    // capture horizontal swipes (see .salon-image-carousel.single-image in
    // SalonImageCarousel.css). The default multi-image touch-action captures
    // horizontal gestures for swipe-to-change-image, which is wrong here.
    if (allImages.length === 0) {
      const placeholderImage = getPlaceholderImage(businessType, primaryCategory);
      return (
        <div className={`salon-image-carousel single-image ${className}`} onClick={onClick}>
          <img
            src={placeholderImage}
            alt={altText}
            className="salon-image-carousel-img salon-image-carousel-placeholder-img"
          />
          {overlay}
        </div>
      );
    }
    return (
      <div className={`salon-image-carousel single-image ${className}`} onClick={onClick}>
        <ProgressiveImage
          src={allImages[0].url}
          alt={altText}
          className="salon-image-carousel-img"
          loading="eager"
        />
        {overlay}
      </div>
    );
  }

  // If no images, show placeholder image based on business type
  if (allImages.length === 0) {
    const placeholderImage = getPlaceholderImage(businessType, primaryCategory);
    return (
      <div className={`salon-image-carousel ${className}`} onClick={onClick}>
        <img
          src={placeholderImage}
          alt={altText}
          className="salon-image-carousel-img salon-image-carousel-placeholder-img"
        />
        {overlay}
      </div>
    );
  }

  // If only one image, show simple image without carousel controls
  if (allImages.length === 1) {
    return (
      <div className={`salon-image-carousel ${className}`} onClick={onClick}>
        <ProgressiveImage
          src={allImages[0].url}
          alt={altText}
          className="salon-image-carousel-img"
          loading="eager"
        />
        {overlay}
      </div>
    );
  }

  // Multiple images - show carousel with swipe support
  return (
    <div
      ref={containerRef}
      className={`salon-image-carousel ${className}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onClick={handleClick}
    >
      <div
        className="salon-image-carousel-track"
        style={{
          transform: `translateX(calc(-${currentIndex * 100}% + ${dragDelta}px))`,
          // 1:1 finger tracking while dragging; the eased snap is for release.
          transition: dragDelta !== 0 ? 'none' : undefined,
        }}
      >
        {allImages.map((image, index) => (
          <div key={index} className="salon-image-carousel-slide">
            <ProgressiveImage
              src={image.url}
              alt={`${altText} ${index + 1}`}
              className="salon-image-carousel-img"
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </div>
      
      {/* Navigation arrows for desktop */}
      {currentIndex > 0 && (
        <button
          className="salon-image-carousel-nav prev"
          onClick={goToPrev}
          aria-label="Previous image"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}
      {currentIndex < allImages.length - 1 && (
        <button
          className="salon-image-carousel-nav next"
          onClick={goToNext}
          aria-label="Next image"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      )}
      
      {/* Dot indicators */}
      <div className="salon-image-carousel-dots">
        {allImages.map((_, index) => (
          <button
            key={index}
            className={`salon-image-carousel-dot ${index === currentIndex ? 'active' : ''}`}
            onClick={(e) => goToSlide(index, e)}
            aria-label={`Go to image ${index + 1}`}
          />
        ))}
      </div>
      
      {overlay}
    </div>
  );
};

export default SalonImageCarousel;
